/**
 * The initials circle, and the stack of them.
 *
 * `design/DESIGN-SYSTEM.md` §2: "circle, `--mdx-brand` (or `--mdx-slate` /
 * `--mdx-nurture` for de-emphasis), white 700 initials. 38px/14px in headers,
 * 32px/12px in rows."
 *
 * THE THREE COLOURS ARE A CYCLE, not a meaning. `settings-workspaces.html`
 * draws a card's people as brand, slate, nurture in order — it distinguishes
 * one head from the next in a stack where they overlap, and it says nothing
 * about the person. Anything that DID mean something would be a band, and
 * RULE-001 keeps that word for one thing.
 *
 * Story 4.1 built this inside the members screen; Story 4.2 needed the same
 * circle on a card and the same stack beside it, so it moved here.
 */
const TONE = ['bg-mdx-brand', 'bg-mdx-slate', 'bg-mdx-nurture'] as const

/** Two letters, from a name or from the address before the `@`. */
export function initials(value: string): string {
  const source = value.includes('@') ? (value.split('@')[0] ?? value) : value
  const words = source.split(/[\s._-]+/).filter(Boolean)
  const letters = words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : (words[0]?.slice(0, 2) ?? '?')
  return letters.toUpperCase()
}

/**
 * The three sizes the screens draw: rows at 32/12, headers at 38/14, and the
 * profile page's own at 64/22 (`settings-profile.html`, "Your details").
 */
const SIZE = {
  sm: 'size-mdx-avatar-sm text-mdx-12',
  lg: 'size-mdx-avatar-lg text-mdx-14',
  xl: 'size-mdx-64 text-mdx-22',
} as const

export function Avatar({
  name,
  index = 0,
  size = 'sm',
  muted = false,
  ringed = false,
  className = '',
}: {
  name: string
  /** Position in a stack — picks one of the three colours. */
  index?: number
  size?: keyof typeof SIZE
  /** Forces `--mdx-nurture`: an invitation, not a person yet. */
  muted?: boolean
  /** The 2px white ring a stacked avatar wears so overlaps stay legible. */
  ringed?: boolean
  className?: string
}) {
  return (
    <span
      aria-hidden="true"
      className={[
        'flex shrink-0 items-center justify-center rounded-mdx-pill font-mdx-bold text-mdx-white',
        SIZE[size],
        muted ? 'bg-mdx-nurture' : TONE[index % TONE.length],
        ringed ? 'border-2 border-mdx-white' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {initials(name)}
    </span>
  )
}

/**
 * Overlapping heads, then the count in words.
 *
 * The mockup overlaps them by `--mdx-space-8` and puts a `+N` chip last when
 * there are more than fit. The chip is `--mdx-nurture-tint` with slate text —
 * it is a number, not a person, so it does not wear a person's colour.
 */
export function AvatarStack({
  people,
  total,
  label,
}: {
  people: { userId: string; name: string }[]
  total: number
  label: string
}) {
  const overflow = total - people.length
  return (
    <div className="flex items-center gap-mdx-12">
      <span aria-hidden="true" className="flex">
        {people.map((person, index) => (
          <Avatar
            key={person.userId}
            name={person.name}
            index={index}
            ringed
            className={index === 0 ? '' : '-ml-mdx-8'}
          />
        ))}
        {overflow > 0 ? (
          <span className="-ml-mdx-8 flex size-mdx-avatar-sm shrink-0 items-center justify-center rounded-mdx-pill border-2 border-mdx-white bg-mdx-nurture-tint text-mdx-12 font-mdx-bold text-mdx-slate">
            +{overflow}
          </span>
        ) : null}
      </span>
      <span className="text-mdx-13 text-mdx-slate">{label}</span>
    </div>
  )
}
